import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { Box, Typography, useTheme } from '@mui/material';
import { fonts } from '@theme/fontsCustomer';
import { BulletIcon, CheckCircleIcon, PencilIcon } from '@icons/index';
import { useTenantConfigDetail } from '@queryHooks/useTenantConfigs';
import { DuplicateLoginModal } from './DuplicateLoginModal';
import { MfaPolicyModal } from './MfaPolicyModal';

type ModalType = 'MFA_POLICY' | 'DUPLICATE_LOGIN' | null;

export const Security = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const params = useParams();
  const tenantId = params.tenantId as string;

  const [openModal, setOpenModal] = useState<ModalType>(null);
  const { data: tenantConfigDetail } = useTenantConfigDetail(tenantId, 'PREVENT_CONCURRENT_LOGIN', {
    enabled: !!tenantId,
  });
  const { data: mfaPolicyDetail } = useTenantConfigDetail(tenantId, 'ENABLE_MFA', {
    enabled: !!tenantId,
  });

  const duplicateLoginEnabled = tenantConfigDetail === 'true';
  const mfaEnabled = mfaPolicyDetail === 'true';

  const handleOpenModal = (modalType: ModalType) => {
    setOpenModal(modalType);
  };

  const handleCloseModal = () => {
    setOpenModal(null);
  };

  return (
    <Container>
      <PageTitle>{t('Security.title')}</PageTitle>

      <ContentArea>
        <InfoBox>
          <InfoItem>
            <BulletIcon width={6} height={6} color={theme.colors['ic-gray-lighter']} />
            <InfoText>{t('Security.info.mfa')}</InfoText>
          </InfoItem>
          <InfoItem>
            <BulletIcon width={6} height={6} color={theme.colors['ic-gray-lighter']} />
            <InfoText>{t('Security.info.duplicateLogin')}</InfoText>
          </InfoItem>
          <InfoItem>
            <BulletIcon width={6} height={6} color={theme.colors['ic-gray-lighter']} />
            <InfoText>{t('Security.info.policy')}</InfoText>
          </InfoItem>
        </InfoBox>

        <Section>
          <SectionTitle>{t('Security.mfaPolicy.title')}</SectionTitle>
          <SectionContent>
            <ContentRow>
              <ContentLeft>
                <ContentColumn>
                  <ContentMainRow>
                    <MainLabel>{t('Security.mfaPolicy.otp')}</MainLabel>
                    <StatusRow>
                      <CheckCircleIcon
                        width={16}
                        height={16}
                        color={mfaEnabled ? theme.colors['ic-green'] : theme.colors['ic-gray-lighter']}
                      />
                      <StatusText enabled={mfaEnabled}>
                        {mfaEnabled ? t('Security.status.configured') : t('Security.status.notConfigured')}
                      </StatusText>
                    </StatusRow>
                  </ContentMainRow>

                  {mfaEnabled && (
                    <SubDataArea>
                      <SubDataRow>
                        <SubLabel>{t('Security.mfaPolicy.required')}</SubLabel>
                        <SubValue>
                          {mfaEnabled ? t('Security.option.required') : t('Security.option.optional')}
                        </SubValue>
                      </SubDataRow>
                    </SubDataArea>
                  )}
                </ContentColumn>
              </ContentLeft>

              <EditButton onClick={() => handleOpenModal('MFA_POLICY')}>
                <PencilIcon width={16} height={16} color={theme.colors['ic-gray-main']} />
              </EditButton>
            </ContentRow>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>{t('Security.duplicateLogin.title')}</SectionTitle>
          <SectionContent>
            <ContentRow>
              <ContentLeft>
                <ContentColumn>
                  <ContentMainRow>
                    <MainLabel>{t('Security.duplicateLogin.usage')}</MainLabel>
                    <StatusRow>
                      <CheckCircleIcon
                        width={16}
                        height={16}
                        color={duplicateLoginEnabled ? theme.colors['ic-green'] : theme.colors['ic-gray-lighter']}
                      />
                      <StatusText enabled={duplicateLoginEnabled}>
                        {duplicateLoginEnabled ? t('Security.status.enabled') : t('Security.status.disabled')}
                      </StatusText>
                    </StatusRow>
                  </ContentMainRow>
                </ContentColumn>
              </ContentLeft>

              <EditButton onClick={() => handleOpenModal('DUPLICATE_LOGIN')}>
                <PencilIcon width={16} height={16} color={theme.colors['ic-gray-main']} />
              </EditButton>
            </ContentRow>
          </SectionContent>
        </Section>
      </ContentArea>

      <MfaPolicyModal
        open={openModal === 'MFA_POLICY'}
        onClose={handleCloseModal}
        tenantId={tenantId}
        mfaEnabled={mfaEnabled}
      />
      <DuplicateLoginModal
        open={openModal === 'DUPLICATE_LOGIN'}
        tenantId={tenantId}
        onClose={handleCloseModal}
        duplicateLoginEnabled={duplicateLoginEnabled}
      />
    </Container>
  );
};

const Container = styled(Box)`
  width: 100%;
  padding: 42px 32px 32px 32px;
  height: calc(100vh - var(--manager-gnb-height));
`;

const PageTitle = styled(Typography)`
  ${fonts.Headline5}
  color: ${({ theme }) => theme.colors['text-gray-main']};
  margin-bottom: 28px;
` as typeof Typography;

const ContentArea = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 28px;
  width: 100%;
`;

const InfoBox = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 20px 32px;
  background-color: ${({ theme }) => theme.colors['bg-gray-light']};
  border-radius: 3px;
`;

const InfoItem = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

const InfoText = styled(Typography)`
  ${fonts.Body2}
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
` as typeof Typography;

const Section = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const SectionTitle = styled(Typography)`
  ${fonts.Headline7}
  color: ${({ theme }) => theme.colors['text-gray-main']};
` as typeof Typography;

const SectionContent = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 15px 20px;
  border: 1px solid ${({ theme }) => theme.colors['border-gray-lighter']};
  border-radius: 5px;
  box-shadow: 0px 1px 0px 0px rgba(0, 0, 0, 0.02), 0px 0px 3px 0px rgba(0, 0, 0, 0.07);
`;

const ContentRow = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  min-height: 48px;
`;

const ContentLeft = styled(Box)`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ContentColumn = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const ContentMainRow = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MainLabel = styled(Typography)`
  ${fonts.Body1}
  color: ${({ theme }) => theme.colors['text-gray-main']};
` as typeof Typography;

const StatusRow = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const StatusText = styled.span<{ enabled: boolean }>`
  ${fonts.Body3}
  color: ${({ enabled, theme }) => (enabled ? theme.colors['text-green'] : theme.colors['text-gray-light'])};
`;

const SubDataArea = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 24px;
`;

const SubDataRow = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: 20px;
`;

const SubLabel = styled(Typography)`
  ${fonts.Body3}
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
  width: 200px;
  flex-shrink: 0;
` as typeof Typography;

const SubValue = styled(Typography)`
  ${fonts.Body2}
  color: ${({ theme }) => theme.colors['text-gray-main']};
  flex: 1;
` as typeof Typography;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background-color: ${({ theme }) => theme.colors['bg-white']};
  border: 1px solid ${({ theme }) => theme.colors['border-gray-light']};
  border-radius: 2px;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background-color: ${({ theme }) => theme.colors['bg-gray-lighter']};
  }
`;
